import { useCallback, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import type {
  UsersForSubjectQuery,
  UsersForSubjectQueryVariables,
} from '@graphql/__generated__/graphql';
import type { RaFormOnChange } from '@modules/_shared/components/form/RaForm';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { getDefaultSubject } from '@modules/_shared/utils/activity';
import { isNullOrEmpty } from '@modules/_shared/utils/strings';
import { useLocale } from '@shared/hooks/locale';

type SubjectAutoUpdateOptions = {
  activityType: 'call' | 'visit' | 'task';
  defaultSubject: string;
};

type LeadActivityFormFields = {
  subject: string;
  assigned_to: string | null | undefined;
  contact_ids: string[] | string | null | undefined;
};

const USERS_FOR_SUBJECT_QUERY = gql(/* GraphQL */ `
  query UsersForSubject($ids: [uuid!]!) {
    users(where: { id: { _in: $ids } }) {
      id
      first_name
      last_name
      display_name
      default_team {
        name
      }
      emails(order_by: [{ primary: desc }, { verified: desc }], limit: 1) {
        id
        email
        primary
      }
    }
  }
`);

export const useLeadActivitySubjectAutoUpdate = ({
  activityType,
  defaultSubject,
}: SubjectAutoUpdateOptions) => {
  const { t } = useLocale();
  const client = useApolloClient();
  const lastAutoSubjectRef = useRef(defaultSubject);
  const manualSubjectRef = useRef(false);

  useEffect(() => {
    lastAutoSubjectRef.current = defaultSubject;
    manualSubjectRef.current = false;
  }, [defaultSubject]);

  const updateSubject = useCallback(
    async (
      assignedId: string | null | undefined,
      contactIds: string[],
      helpers: Parameters<RaFormOnChange<LeadActivityFormFields>>[3],
    ) => {
      // Collect all user IDs we need to fetch
      const allIds = [assignedId, ...contactIds].filter(
        (id): id is string => !isNullOrEmpty(id, true),
      );

      if (allIds.length === 0) {
        // No users to fetch, generate subject with empty data
        const newSubject = getDefaultSubject({
          t,
          activityType,
          assignedTo: { firstName: null, lastName: null, teamName: null },
          participants: [],
        });

        const values = helpers.getValues();
        if (values.subject !== newSubject) {
          helpers.setValue('subject', newSubject, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        lastAutoSubjectRef.current = newSubject;
        return;
      }

      // Fetch users
      try {
        const { data } = await client.query<
          UsersForSubjectQuery,
          UsersForSubjectQueryVariables
        >({
          query: USERS_FOR_SUBJECT_QUERY,
          variables: { ids: allIds },
          fetchPolicy: 'cache-first',
        });

        const usersById = new Map(
          data?.users?.map(user => [user.id, user]) ?? [],
        );

        // Get assigned user
        const assignedUser = assignedId ? usersById.get(assignedId) : undefined;
        const assignedTo = {
          firstName: assignedUser?.first_name ?? assignedUser?.display_name,
          lastName: assignedUser?.last_name,
          teamName: assignedUser?.default_team?.name,
        };

        // Get participants
        const participants = contactIds
          .map(id => usersById.get(id))
          .filter((user): user is NonNullable<typeof user> => user != null)
          .map(user => ({
            name: getUserDisplayName(user, true),
          }));

        const newSubject = getDefaultSubject({
          t,
          activityType,
          assignedTo,
          participants,
        });

        const values = helpers.getValues();
        const currentSubject = values.subject;

        lastAutoSubjectRef.current = newSubject;

        if (currentSubject === newSubject) {
          manualSubjectRef.current = false;
          return;
        }

        helpers.setValue('subject', newSubject, {
          shouldDirty: false,
          shouldValidate: false,
        });
      } catch (error) {
        console.error('Failed to fetch users for subject generation:', error);
      }
    },
    [activityType, client, t],
  );

  const handleChange = useCallback<RaFormOnChange<LeadActivityFormFields>>(
    (_data, name, _type, helpers) => {
      const values = helpers.getValues();

      if (name === 'subject') {
        const current = values.subject;
        manualSubjectRef.current = current !== lastAutoSubjectRef.current;
        return;
      }

      if (name !== 'assigned_to' && name !== 'contact_ids') {
        return;
      }

      if (manualSubjectRef.current) {
        return;
      }

      const assignedId = values.assigned_to;
      const rawContactIds = values.contact_ids;
      const contactIds = Array.isArray(rawContactIds)
        ? rawContactIds.filter((id): id is string => !isNullOrEmpty(id, true))
        : !isNullOrEmpty(rawContactIds, true)
        ? [rawContactIds]
        : [];

      updateSubject(assignedId, contactIds, helpers).catch(error => {
        console.error('Failed to update subject:', error);
      });
    },
    [updateSubject],
  );

  return handleChange;
};
